import { useCallback, useMemo, useRef, useState } from 'react';
import {
  useMyChecklistStore,
  openPastDayGroups,
  openTasksFor,
  toOpenDayGroups,
  type OpenChecklistDayGroup,
} from '../store/useMyChecklistStore';

/**
 * Wraps a clock-out action: refreshes today's checklists and everything still open from past
 * days first and, if anything is unconfirmed, opens a warning dialog instead of clocking out
 * right away. The employee can tick items off there or proceed anyway — clocking out is never
 * blocked (ADR-0010, ADR-0017).
 */
export function useClockOutGuard(todayIso: string, doClockOut: () => Promise<void> | void) {
  const fetchDay = useMyChecklistStore((s) => s.fetchDay);
  const fetchOpenPast = useMyChecklistStore((s) => s.fetchOpenPast);
  // why: subscribe to the stored objects, not to a derived array — a selector returning a
  // fresh array on every store read makes useSyncExternalStore loop.
  const day = useMyChecklistStore((s) => s.days[todayIso]);
  const openPast = useMyChecklistStore((s) => s.openPast);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [checking, setChecking] = useState(false);
  const clockOutRef = useRef(doClockOut);
  clockOutRef.current = doClockOut;

  // Today first — that is the work the employee has in mind at clock-out — then the past days,
  // newest to oldest, each under its own date heading so it cannot be mistaken for today.
  const dayGroups: OpenChecklistDayGroup[] = useMemo(() => {
    const todayTasks = (day?.tasks ?? []).filter((task) =>
      (task.items ?? []).some((i) => !i.isCompleted),
    );
    const past = toOpenDayGroups(openPast, todayIso);

    return todayTasks.length > 0 ? [{ date: todayIso, tasks: todayTasks }, ...past] : past;
  }, [day, openPast, todayIso]);

  const guardClockOut = useCallback(async () => {
    setChecking(true);
    try {
      // Refetch rather than trust cached state — the employee may have ticked items off
      // on another device, and warning about already-done work erodes the warning.
      // A failing read must not keep anyone from clocking out, hence allSettled.
      await Promise.allSettled([fetchDay(todayIso), fetchOpenPast()]);

      const state = useMyChecklistStore.getState();
      if (openTasksFor(state, todayIso).length > 0 || openPastDayGroups(state, todayIso).length > 0) {
        setDialogOpen(true);
        return;
      }
    } finally {
      setChecking(false);
    }

    await clockOutRef.current();
  }, [fetchDay, fetchOpenPast, todayIso]);

  const clockOutAnyway = useCallback(async () => {
    setDialogOpen(false);
    await clockOutRef.current();
  }, []);

  const dismissDialog = useCallback(() => setDialogOpen(false), []);

  return { guardClockOut, checking, dialogOpen, dayGroups, clockOutAnyway, dismissDialog };
}
